import { normalizeCodeInput, normalizeDigits } from '@saay/formatting';
import { useOnlineStatus } from '../kit.js';
import type { ReactNode } from 'react';

/**
 * Admin-only form helpers. The form pieces (NativeSelect, TextArea, Checkbox, SolarDateInput,
 * FieldErrorList, CommandFeedback, and `applyFieldErrors`) live in `@saay/ui`.
 */

/** React Hook Form defaults for command and setup forms (XAP-19). */
export const ADMIN_FORM_OPTIONS = {
  mode: 'onTouched',
  reValidateMode: 'onChange',
} as const;

/** Disables financial and setup mutations while offline (FTD §11). */
export function useSubmitBlocked(): { readonly blocked: boolean; readonly reason?: string } {
  const online = useOnlineStatus();
  return online
    ? { blocked: false }
    : { blocked: true, reason: 'تا وصل شدن اینترنت، ثبت ممکن نیست.' };
}

/** Save stays off until there is a real change or the network blocks submit (XAP-19). */
export function mutationSubmitDisabled(
  offline: { readonly blocked: boolean },
  hasChanges: boolean,
  pending = false,
): boolean {
  return offline.blocked || pending || !hasChanges;
}

/** Normalized digits for numeric identifiers (national code, national ID) before validation. */
export function codeInput(value: string): string {
  return normalizeCodeInput(value);
}

/**
 * Canonical form for administrative codes (D-107): Persian and Arabic digits become ASCII and letters
 * upper-case; hyphens and underscores stay (unlike `codeInput`, which is for numeric identifiers).
 */
export function normalizeAdminCode(value: string): string {
  return normalizeDigits(value).trim().toUpperCase();
}

/** Decimal separators a Persian or Latin keyboard may type: «.», «٫», «/», «،», «,». */
const DECIMAL_SEPARATOR = /[.٫/،,]/;

/**
 * A typed decimal in the API's fixed form: Persian digits become ASCII, any keyboard's decimal
 * separator becomes «.», and the fraction is padded to `places` («۷/۵» → "7.50", «7» → "7.00").
 * Anything that is not a plain decimal comes back trimmed and unchanged, so the schema still
 * shows its message.
 */
export function normalizeDecimalInput(raw: unknown, places: number): string {
  const text = normalizeDigits(String(raw ?? ''))
    .trim()
    .replace(DECIMAL_SEPARATOR, '.');
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
  const whole = match?.[1];
  const fraction = match?.[2] ?? '';
  if (whole === undefined || fraction.length > places) return text;
  const integer = whole.replace(/^0+(?=\d)/, '');
  return places === 0 ? integer : `${integer}.${fraction.padEnd(places, '0')}`;
}

/** One possible choice: show a fixed row instead of a one-item dropdown (XAP-18). */
export function ResolvedChoice({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}) {
  return (
    <div className="saay-field saay-field--resolved">
      <span className="saay-field__label">{label}</span>
      <p className="saay-field__resolved">{value}</p>
    </div>
  );
}
